import { CommentIcon, Pencil, TrashIcon } from 'assets/icons';
import EmployeeAvatar from 'components/assign/EmployeeAvatar';
import { cardIconButtonClass } from 'components/card/PanelCard';
import Tooltip from 'components/tooltips/Tooltips';
import { clockLabel, dayLabel } from 'helper/timeLabels';
import useText from 'hooks/useText';
import { useState } from 'react';
import CommentComposer from './CommentComposer';
import CommentDeleteDialog from './CommentDeleteDialog';
import { COMMENT_ACTIONS_REVEAL_CLASS, COMMENT_ROW_CLASS } from './commentActionsClass';
import MentionText from './MentionText';

/**
 * The comments of a task, oldest first: who, when, and the text in a grey
 * bubble. With none yet, an invitation centred in the space left. `people`
 * colours the colleagues the comments name.
 *
 * A comment whose `canEdit` the api set is edited in its line, one at a time,
 * through `onEdit(commentId, body, mentions)`; one whose `canDelete` it set is
 * deleted after a confirmation, through `onDelete(commentId)`. Both resolve
 * true once done. `pendingIds` are the comments with a request under way.
 */
const CommentThread = ({ comments, people, meId, onEdit, onDelete, pendingIds = [] }) => {
  const { text } = useText('comments');
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  if (!comments) return null;
  if (!comments.length) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-6 text-center">
        <span className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-700">
          <CommentIcon wh={18} color="currentColor" />
        </span>
        <p className="text-sm font-medium text-gray-900">{text('emptyTitle')}</p>
        <p className="max-w-md text-sm text-gray-600">{text('emptyText')}</p>
      </div>
    );
  }
  return (
    <ol className="flex flex-col gap-4">
      {comments.map((comment) => {
        const at = new Date(comment.createdAt);
        const isEditing = editingId === comment.id;
        const isPending = pendingIds.includes(comment.id);
        // Strictly true: the api decides who may edit or delete, an absent flag hides it.
        const canEdit = comment.canEdit === true && Boolean(onEdit) && !isEditing;
        const canDelete = comment.canDelete === true && Boolean(onDelete) && !isEditing;
        return (
          <li key={comment.id} className={`${COMMENT_ROW_CLASS} flex gap-3`}>
            <EmployeeAvatar employee={comment.author} size={32} />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-2">
                <p className="min-w-0 flex-1 text-sm">
                  <span className="font-medium text-gray-900">
                    {comment.author ? `${comment.author.firstname} ${comment.author.lastname}`.trim() : text('someone')}
                  </span>{' '}
                  <span className="text-xs text-gray-500">
                    {text('at', { day: dayLabel(at), time: clockLabel(at) })}
                  </span>
                  {comment.editedAt && <span className="ml-1 text-xs text-gray-500">{text('edited')}</span>}
                </p>
                {(canEdit || canDelete) && (
                  <div className={`flex gap-1 ${COMMENT_ACTIONS_REVEAL_CLASS}`}>
                    {canEdit && (
                      <Tooltip text={text('edit')} placement="top">
                        <button
                          type="button"
                          className={cardIconButtonClass}
                          aria-label={text('edit')}
                          disabled={isPending}
                          onClick={() => setEditingId(comment.id)}
                        >
                          <Pencil wh={18} color="#374151" />
                        </button>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip text={text('delete')} placement="top">
                        <button
                          type="button"
                          className={`${cardIconButtonClass} disabled:cursor-not-allowed disabled:opacity-40`}
                          aria-label={text('delete')}
                          disabled={isPending}
                          onClick={() => setDeletingId(comment.id)}
                        >
                          <TrashIcon wh={18} color="#374151" />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
              {isEditing ? (
                <div className="mt-1">
                  <CommentComposer
                    people={people}
                    meId={meId}
                    initialBody={comment.body}
                    onCancel={() => setEditingId(null)}
                    onSend={async (body, mentions) => {
                      const saved = await onEdit(comment.id, body, mentions);
                      if (saved) setEditingId(null);
                      return saved;
                    }}
                  />
                </div>
              ) : (
                <p className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900">
                  <MentionText text={comment.body} people={people} />
                </p>
              )}
            </div>
          </li>
        );
      })}
      <CommentDeleteDialog
        open={Boolean(deletingId)}
        busy={pendingIds.includes(deletingId)}
        onCancel={() => setDeletingId(null)}
        onConfirm={async () => {
          const id = deletingId;
          await onDelete(id);
          // Deleted or not, the dialog closes: a failure is toasted.
          setDeletingId((current) => (current === id ? null : current));
        }}
      />
    </ol>
  );
};

export default CommentThread;
